import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  BrokenImage as BrokenImageIcon,
} from "@mui/icons-material";
import { useImageArtifact } from "../../hooks/useImageArtifact";
import { formatPageNumber } from "../../utils/imageSourceUtils";

type SourceImageThumbnailProps = {
  imageArtifactId: number;
  fileName?: string;
  pageNumber?: number | number[];
  /** small = 48px (list view), medium = 200px (expanded view) */
  size?: "small" | "medium";
  onClick?: () => void;
  /** Show caption with document context below the image */
  showCaption?: boolean;
};

/**
 * Thumbnail component for displaying source images from artifacts.
 * Fetches the image via the artifact API and displays with loading/error states.
 * Supports click-to-expand modal for full-size viewing.
 */
const SourceImageThumbnail: React.FC<SourceImageThumbnailProps> = ({
  imageArtifactId,
  fileName,
  pageNumber,
  size = "medium",
  onClick,
  showCaption = false,
}) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);

  const {
    data: imageUrl,
    isLoading,
    isError,
  } = useImageArtifact(imageArtifactId);

  const dimensions =
    size === "small" ? { width: 48, height: 48 } : { width: 200, height: 150 };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (imageUrl) {
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        data-testid={`aa-source-image-thumbnail-loading-${imageArtifactId}`}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: dimensions.width,
          height: dimensions.height,
          backgroundColor: theme.palette.action.hover,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CircularProgress size={size === "small" ? 16 : 24} />
      </Box>
    );
  }

  // Error state
  if (isError || !imageUrl) {
    return (
      <Box
        data-testid={`aa-source-image-thumbnail-error-${imageArtifactId}`}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: dimensions.width,
          height: dimensions.height,
          backgroundColor: theme.palette.action.hover,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.secondary,
        }}
      >
        <BrokenImageIcon fontSize={size === "small" ? "small" : "medium"} />
      </Box>
    );
  }

  return (
    <>
      <Box
        data-testid={`aa-source-image-thumbnail-${imageArtifactId}`}
        sx={{ display: "inline-block" }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={fileName ? `Image from ${fileName}` : "Source image"}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick();
            }
          }}
          sx={{
            width: dimensions.width,
            height: dimensions.height,
            objectFit: "cover",
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover, &:focus-visible": {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
              transform: "scale(1.02)",
              outline: "none",
            },
          }}
        />
        {showCaption && (fileName || pageNumber) && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              color: theme.palette.text.secondary,
              maxWidth: dimensions.width,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fileName && <span>{fileName}</span>}
            {fileName && pageNumber && <span> - </span>}
            {pageNumber && <span>Page {formatPageNumber(pageNumber)}</span>}
          </Typography>
        )}
      </Box>

      {/* Full-size image modal */}
      <Dialog
        data-testid={`aa-source-image-modal-${imageArtifactId}`}
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <IconButton
          data-testid={`aa-source-image-modal-close-${imageArtifactId}`}
          aria-label="Close image preview"
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(0,0,0,0.5)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          <Box
            component="img"
            data-testid={`aa-source-image-modal-fullsize-${imageArtifactId}`}
            src={imageUrl}
            alt={fileName ? `Image from ${fileName}` : "Source image"}
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SourceImageThumbnail;
